// src/features/admin/components/settings/integrations/components/CommunicationTab.tsx
// SMS_07: Tenant Communications dashboard inside Firm Settings → Integrations
import React, { useState, useEffect } from 'react';
import {
  ClockIcon,
  DevicePhoneMobileIcon,
  CheckCircleIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';
import axios from 'axios';

interface TenantDashboard {
  connected: boolean;
  status: string;
  numbers: Array<{ id: string; number: string; type: string; default: boolean; status: string }>;
  usageThisMonth: { outbound: number; inbound: number; failed: number };
  usageToday: { outbound: number; inbound: number; failed: number };
  recentConversations: Array<{ id: string; remoteNumber: string; lastMessageAt: string | null; status: string; messageCount: number }>;
  optOutCount: number;
}

const COMMUNICATION_PROVIDERS = [
  { name: 'Microsoft Teams', description: 'Video conferencing and team chat.', features: ['Video calls', 'Chat', 'Calendar integration'] },
  { name: 'Zoom', description: 'Meeting scheduling from EdgeLex.', features: ['Meeting scheduling', 'One-click join', 'Recording'] },
  { name: 'Slack', description: 'Team notifications and channels.', features: ['Notifications', 'Matter channels', 'Search'] },
  { name: 'RingCentral', description: 'VoIP phone system integration.', features: ['Click-to-call', 'Call logging', 'Voicemail'] },
];

const CommunicationTab: React.FC = () => {
  const [data, setData] = useState<TenantDashboard | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await axios.get('/api/v1/communications/tenant/dashboard');
      setData(res.data);
    } catch {
      // Not configured yet — show default state
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  return (
    <div className="space-y-6">
      {/* SMS Communications — Live Section */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center">
            <DevicePhoneMobileIcon className="h-6 w-6 text-lex-accent mr-3" />
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">SMS Communications</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">Client messaging, reminders, and two-way SMS</p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <button onClick={fetchData} className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700">
              <ArrowPathIcon className={`h-4 w-4 text-gray-400 ${loading ? 'animate-spin' : ''}`} />
            </button>
            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
              data?.connected
                ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300'
                : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
            }`}>
              {data?.connected ? (
                <><CheckCircleIcon className="h-3.5 w-3.5 mr-1" />Connected</>
              ) : 'Not Connected'}
            </span>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
          <StatBox label="Status" value={data?.status || 'Not configured'} />
          <StatBox label="Numbers" value={data?.numbers?.length ? String(data.numbers.length) : '—'} />
          <StatBox label="Sent This Month" value={String(data?.usageThisMonth?.outbound ?? '—')} />
          <StatBox label="Opt-Outs" value={String(data?.optOutCount ?? '—')} />
        </div>

        {/* Numbers */}
        {data?.numbers && data.numbers.length > 0 && (
          <div className="mb-4">
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Your Numbers</h4>
            <div className="space-y-1">
              {data.numbers.map(n => (
                <div key={n.id} className="flex items-center justify-between py-1.5 px-3 bg-gray-50 dark:bg-gray-700/50 rounded">
                  <span className="text-sm font-mono text-gray-900 dark:text-white">{n.number}</span>
                  <div className="flex items-center space-x-2">
                    {n.default && <span className="text-xs px-1.5 py-0.5 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded">Default</span>}
                    <span className="text-xs text-gray-400">{n.type}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Recent Conversations */}
        {data?.recentConversations && data.recentConversations.length > 0 && (
          <div className="mb-4">
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Recent Conversations</h4>
            <div className="space-y-1">
              {data.recentConversations.slice(0, 5).map(c => (
                <div key={c.id} className="flex items-center justify-between py-1.5 px-3 bg-gray-50 dark:bg-gray-700/50 rounded">
                  <div>
                    <span className="text-sm font-mono text-gray-900 dark:text-white">{c.remoteNumber}</span>
                    <span className="text-xs text-gray-400 ml-2">{c.messageCount} msg{c.messageCount !== 1 ? 's' : ''}</span>
                  </div>
                  <span className="text-xs text-gray-400">
                    {c.lastMessageAt ? new Date(c.lastMessageAt).toLocaleDateString() : '—'}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Today's Usage */}
        {data?.connected && (
          <div className="grid grid-cols-3 gap-3 pt-3 border-t border-gray-200 dark:border-gray-700">
            <div className="text-center">
              <p className="text-lg font-bold text-gray-900 dark:text-white">{data.usageToday.outbound}</p>
              <p className="text-xs text-gray-400">Sent Today</p>
            </div>
            <div className="text-center">
              <p className="text-lg font-bold text-gray-900 dark:text-white">{data.usageToday.inbound}</p>
              <p className="text-xs text-gray-400">Received Today</p>
            </div>
            <div className="text-center">
              <p className="text-lg font-bold text-red-500">{data.usageToday.failed}</p>
              <p className="text-xs text-gray-400">Failed Today</p>
            </div>
          </div>
        )}

        {!data?.connected && (
          <div className="pt-3 border-t border-gray-200 dark:border-gray-700">
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Contact your EdgeLex administrator to enable SMS communications for your firm.
            </p>
          </div>
        )}
      </div>

      {/* Coming Soon — Other Integrations */}
      <div className="bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800 rounded-lg p-6 text-center">
        <ClockIcon className="h-10 w-10 text-amber-600 dark:text-amber-400 mx-auto mb-3" />
        <h3 className="text-md font-semibold text-amber-800 dark:text-amber-200 mb-1">More Integrations Coming</h3>
        <p className="text-sm text-amber-700 dark:text-amber-300">Video conferencing, team chat, and phone systems.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {COMMUNICATION_PROVIDERS.map((provider, i) => (
          <div key={i} className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 opacity-60">
            <h5 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">{provider.name}</h5>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">{provider.description}</p>
            <div className="flex flex-wrap gap-1">
              {provider.features.map((f, j) => (
                <span key={j} className="text-xs px-2 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 rounded">{f}</span>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

function StatBox({ label, value }: { label: string; value: string }) {
  return (
    <div className="text-center p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
      <p className="text-xs text-gray-500 dark:text-gray-400">{label}</p>
      <p className="text-sm font-medium text-gray-900 dark:text-white">{value}</p>
    </div>
  );
}

export default CommunicationTab;
